/**
 * /projects/:projectSlug/envs/:envSlug/secrets/import — bulk-import a .env
 * block as individual secrets.
 *
 * The user pastes a KEY=VALUE block; we parse it client-side, render an
 * editable preview table (path / value / content-type / status), and on
 * submit fire `secrets.create` once per row. Conflicts with existing paths
 * are flagged with a ⚠ icon, and the submit button is disabled until every
 * conflict and validation error is resolved — there is no "silently
 * overwrite" path here.
 *
 * Import is sequential (concurrency 1) so per-row status reflects exactly
 * what landed server-side. If a row fails mid-batch, earlier rows stay
 * committed; the user sees a per-row error and can retry the failed rows
 * by editing them and re-submitting. We refresh the conflict list against
 * the server after a partial run so already-committed rows flip to "done".
 */

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type CreateSecretBody,
  WardkeyAPIError,
  useActiveOrg,
  useApiClient,
  useApiQuery,
} from "@/lib/api";
import { type ParsedRow, parseDotenv } from "@/lib/dotenv";
import { useToast } from "@/lib/toast";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { validatePath } from "./secrets-new";

const MAX_VALUE_BYTES = 64 * 1024;
const MAX_PATH_LEN = 256;

const CONTENT_TYPES = [
  { value: "text/plain", label: "Plain text" },
  { value: "application/json", label: "JSON" },
  { value: "application/octet-stream", label: "Binary" },
  { value: "application/x-pem-file", label: "PEM" },
] as const;

type RowStatus =
  | { kind: "pending" }
  | { kind: "running" }
  | { kind: "done" }
  | { kind: "failed"; message: string };

type ImportRow = {
  id: string;
  lineNumber: number;
  path: string;
  value: string;
  contentType: string;
  parseError: string | null;
  status: RowStatus;
};

const EXAMPLE_PASTE = `DATABASE_URL=postgres://user:pass@host:5432/db
STRIPE_SECRET_KEY=sk_test_abc123
# comments and blank lines are ignored
AWS_REGION=us-east-1`;

export function SecretsImportRoute() {
  const activeOrg = useActiveOrg();
  const params = useParams<{ projectSlug: string; envSlug: string }>();
  const projectSlug = params.projectSlug ?? "";
  const envSlug = params.envSlug ?? "";

  if (!projectSlug || !envSlug) {
    return (
      <div className="mx-auto max-w-[700px]">
        <div className="fade-up rounded-xl border border-dashed border-border bg-card/40 p-10 text-center">
          <p className="text-[14px] text-muted-foreground">
            Missing project or environment in the URL.{" "}
            <Link to="/projects" className="text-foreground underline-offset-4 hover:underline">
              Pick a project
            </Link>{" "}
            first.
          </p>
        </div>
      </div>
    );
  }

  return <ImportForm orgId={activeOrg.org_id} project={projectSlug} env={envSlug} />;
}

function ImportForm({ orgId, project, env }: { orgId: string; project: string; env: string }) {
  const { pushToast } = useToast();
  const client = useApiClient();
  const [paste, setPaste] = useState("");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [running, setRunning] = useState(false);

  const existing = useApiQuery(
    ["secrets-for-import", orgId, project, env],
    (c) => c.secrets.list(orgId, project, env),
    { suppressToast: true },
  );
  const existingPaths = useMemo(() => {
    const set = new Set<string>();
    for (const s of existing.data?.secrets ?? []) set.add(s.path);
    return set;
  }, [existing.data]);

  // Re-parse whenever the textarea changes. Re-derive rows so per-row state
  // (status, edited path) is reset — pasting a new block means starting over.
  useEffect(() => {
    if (paste.length === 0) {
      setRows([]);
      return;
    }
    const parsed = parseDotenv(paste);
    setRows(parsed.map(parsedToRow));
  }, [paste]);

  // Derived per-row signals — recomputed on every render. Cheap; rows are
  // bounded by what fits in a paste.
  const annotated = useMemo(
    () => rows.map((r) => annotate(r, existingPaths)),
    [rows, existingPaths],
  );

  const counts = useMemo(() => countByState(annotated), [annotated]);
  const submitDisabled =
    running ||
    annotated.length === 0 ||
    counts.parseErrors > 0 ||
    counts.pathErrors > 0 ||
    counts.conflicts > 0 ||
    counts.valueErrors > 0 ||
    counts.readyToCreate === 0;

  function updateRow(id: string, patch: Partial<ImportRow>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function removeRow(id: string) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  async function runImport() {
    setRunning(true);
    // Snapshot the row ids in current order so we walk them sequentially even
    // if state updates re-order under us (they shouldn't, but be defensive).
    const order = rows.map((r) => r.id);
    let okCount = 0;
    let failCount = 0;
    for (const id of order) {
      // Re-read the latest row from state so inline edits made just before
      // submit are picked up. setRows callbacks flush before the next iter.
      const current = await new Promise<ImportRow | undefined>((resolve) => {
        setRows((prev) => {
          resolve(prev.find((r) => r.id === id));
          return prev;
        });
      });
      if (!current) continue;
      if (current.parseError) continue;
      if (current.status.kind === "done") continue;
      if (existingPaths.has(current.path)) continue;
      if (validatePath(current.path) !== null) continue;
      if (validateValue(current.value) !== null) continue;

      updateRow(id, { status: { kind: "running" } });
      try {
        const body: CreateSecretBody = {
          path: current.path,
          value: current.value,
          content_type: current.contentType,
        };
        await client.secrets.create(orgId, project, env, body);
        updateRow(id, { status: { kind: "done" } });
        okCount += 1;
      } catch (err) {
        const message = extractError(err);
        updateRow(id, { status: { kind: "failed", message } });
        failCount += 1;
      }
    }
    setRunning(false);
    // Refresh the conflict set so any rows that landed during this run become
    // recognized as existing on a subsequent attempt.
    void existing.refetch();
    if (okCount > 0 && failCount === 0) {
      pushToast({ kind: "success", title: `Imported ${okCount} secrets` });
    } else if (okCount > 0 && failCount > 0) {
      pushToast({
        kind: "error",
        title: `Imported ${okCount}, failed ${failCount}`,
        body: "Check the failed rows below and retry.",
      });
    } else if (failCount > 0) {
      pushToast({
        kind: "error",
        title: `Import failed (${failCount} row${failCount === 1 ? "" : "s"})`,
      });
    }
  }

  return (
    <div className="fade-up mx-auto max-w-[1100px]">
      <PageHeader
        title="Import .env"
        subtitle={
          <span className="font-mono text-[12px] text-muted-foreground">
            {project}/{env}
          </span>
        }
      />

      <section>
        <PasteField paste={paste} onPasteChange={setPaste} disabled={running} />
      </section>

      {rows.length > 0 && (
        <section className="mt-8">
          <PreviewHeader
            counts={counts}
            totalRows={annotated.length}
            existingLoading={existing.isLoading}
          />
          <PreviewTable
            rows={annotated}
            onRowChange={updateRow}
            onRowRemove={removeRow}
            disabled={running}
          />

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link
                to={`/projects/${encodeURIComponent(project)}/envs/${encodeURIComponent(env)}/secrets`}
              >
                Cancel
              </Link>
            </Button>
            <Button type="button" size="sm" onClick={runImport} disabled={submitDisabled}>
              {running
                ? "Importing…"
                : `Import ${counts.readyToCreate} secret${counts.readyToCreate === 1 ? "" : "s"}`}
              <span aria-hidden>→</span>
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function PasteField({
  paste,
  onPasteChange,
  disabled,
}: {
  paste: string;
  onPasteChange: (next: string) => void;
  disabled: boolean;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          paste .env
        </span>
        {paste.length === 0 && (
          <button
            type="button"
            onClick={() => onPasteChange(EXAMPLE_PASTE)}
            disabled={disabled}
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            insert example
          </button>
        )}
      </div>
      <Textarea
        aria-label="Paste .env block"
        value={paste}
        onChange={(e) => onPasteChange(e.target.value)}
        placeholder={"FOO=bar\nBAZ=qux"}
        rows={paste.length === 0 ? 8 : 12}
        spellCheck={false}
        disabled={disabled}
        className="mt-2 resize-y font-mono"
      />
    </label>
  );
}

function PreviewHeader({
  counts,
  totalRows,
  existingLoading,
}: {
  counts: Counts;
  totalRows: number;
  existingLoading: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-3">
      <div>
        <h2 className="font-sans text-xl font-medium text-foreground">Preview</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Review each row before importing. Edit the path inline to resolve conflicts.
        </p>
      </div>
      <div
        aria-live="polite"
        className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-widest"
      >
        <Pill tone="default">{totalRows} parsed</Pill>
        <Pill tone="ready">{counts.readyToCreate} ready</Pill>
        {counts.conflicts > 0 && <Pill tone="warning">{counts.conflicts} conflict</Pill>}
        {(counts.parseErrors > 0 || counts.pathErrors > 0 || counts.valueErrors > 0) && (
          <Pill tone="danger">
            {counts.parseErrors + counts.pathErrors + counts.valueErrors} invalid
          </Pill>
        )}
        {existingLoading && <Pill tone="muted">checking conflicts…</Pill>}
      </div>
    </div>
  );
}

function Pill({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "default" | "ready" | "warning" | "danger" | "muted";
}) {
  const cls =
    tone === "ready"
      ? "border-border bg-muted text-foreground"
      : tone === "warning"
        ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-200"
        : tone === "danger"
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : tone === "muted"
            ? "border-border bg-muted text-muted-foreground"
            : "border-border bg-muted text-muted-foreground";
  return <span className={`rounded-sm border px-2 py-0.5 ${cls}`}>{children}</span>;
}

type AnnotatedRow = ImportRow & {
  pathError: string | null;
  valueError: string | null;
  conflict: boolean;
};

const ROW_GRID = "grid grid-cols-[24px_1.4fr_2fr_140px_120px] items-center gap-3";

function PreviewTable({
  rows,
  onRowChange,
  onRowRemove,
  disabled,
}: {
  rows: ReadonlyArray<AnnotatedRow>;
  onRowChange: (id: string, patch: Partial<ImportRow>) => void;
  onRowRemove: (id: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-border bg-card">
      {/* Column header — not a row; a ledger label strip aligned to the grid. */}
      <div
        className={`${ROW_GRID} border-b border-border bg-muted/30 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70`}
      >
        <span aria-hidden />
        <span>path</span>
        <span>value</span>
        <span>type</span>
        <span className="text-right">actions</span>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((row) => (
          <PreviewRow
            key={row.id}
            row={row}
            onChange={(patch) => onRowChange(row.id, patch)}
            onRemove={() => onRowRemove(row.id)}
            disabled={disabled}
          />
        ))}
      </ul>
    </div>
  );
}

function PreviewRow({
  row,
  onChange,
  onRemove,
  disabled,
}: {
  row: AnnotatedRow;
  onChange: (patch: Partial<ImportRow>) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const [showValue, setShowValue] = useState(false);
  const isError = row.parseError !== null;
  const issue = row.parseError ?? row.pathError ?? row.valueError ?? null;
  const status = row.status;

  return (
    <li
      data-testid={`import-row-${row.id}`}
      className={`group ${ROW_GRID} px-3 py-2 transition-colors ${
        isError ? "bg-destructive/5" : row.conflict ? "bg-yellow-500/[0.03]" : "hover:bg-muted/40"
      }`}
    >
      <StatusIcon status={status} conflict={row.conflict} hasError={issue !== null} />

      <div className="min-w-0">
        {isError ? (
          <div
            className="truncate font-mono text-[12px] text-destructive"
            title={row.parseError ?? ""}
          >
            line {row.lineNumber}: {row.parseError}
          </div>
        ) : (
          <Input
            type="text"
            aria-label={`Path for line ${row.lineNumber}`}
            value={row.path}
            onChange={(e) => onChange({ path: e.target.value, status: { kind: "pending" } })}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            disabled={disabled || status.kind === "done"}
            className={`h-auto px-2 py-1 font-mono text-[12px] ${
              row.pathError || row.conflict ? "border-yellow-500/50" : ""
            }`}
          />
        )}
        {!isError && (row.pathError || row.conflict) && (
          <div className="mt-1 font-mono text-[10px] text-yellow-200">
            {row.pathError ?? "A secret already lives at this path. Rename to import."}
          </div>
        )}
      </div>

      <div className="min-w-0">
        {!isError && (
          <div className="flex items-center gap-2">
            <div className="flex-1 truncate font-mono text-[12px] text-muted-foreground">
              {showValue ? row.value : maskValue(row.value)}
            </div>
            <button
              type="button"
              onClick={() => setShowValue((v) => !v)}
              className="shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
              aria-label={showValue ? "Hide value" : "Show value"}
            >
              {showValue ? "hide" : "show"}
            </button>
          </div>
        )}
        {!isError && row.valueError && (
          <div className="mt-1 font-mono text-[10px] text-destructive">{row.valueError}</div>
        )}
        {status.kind === "failed" && (
          <div className="mt-1 font-mono text-[10px] text-destructive">{status.message}</div>
        )}
      </div>

      <div>
        {!isError && (
          <select
            aria-label={`Content type for line ${row.lineNumber}`}
            value={row.contentType}
            onChange={(e) => onChange({ contentType: e.target.value })}
            disabled={disabled || status.kind === "done"}
            className="w-full rounded-md border border-input bg-card px-2 py-1 font-mono text-[11px] text-foreground outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50"
          >
            {CONTENT_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        {status.kind === "done" ? (
          <span className="font-mono text-[10px] uppercase tracking-widest text-foreground">
            done
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onRemove}
            disabled={disabled}
            className="text-muted-foreground opacity-70 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
          >
            remove
          </Button>
        )}
      </div>
    </li>
  );
}

function StatusIcon({
  status,
  conflict,
  hasError,
}: {
  status: RowStatus;
  conflict: boolean;
  hasError: boolean;
}) {
  if (status.kind === "done") {
    return (
      <span aria-label="done" className="text-[14px] leading-none text-foreground">
        ✓
      </span>
    );
  }
  if (status.kind === "running") {
    return (
      <span aria-label="running" className="text-[14px] leading-none text-muted-foreground">
        …
      </span>
    );
  }
  if (status.kind === "failed") {
    return (
      <span aria-label="failed" className="text-[14px] leading-none text-destructive">
        ✕
      </span>
    );
  }
  if (hasError || conflict) {
    return (
      <span aria-label="warning" className="text-[14px] leading-none text-yellow-300">
        !
      </span>
    );
  }
  return (
    <span aria-label="ready" className="text-[14px] leading-none text-muted-foreground/70">
      ·
    </span>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function parsedToRow(p: ParsedRow, idx: number): ImportRow {
  const id = `row-${idx}-${p.lineNumber}`;
  if (p.kind === "error") {
    return {
      id,
      lineNumber: p.lineNumber,
      path: p.key ?? "",
      value: "",
      contentType: "text/plain",
      parseError: p.error,
      status: { kind: "pending" },
    };
  }
  return {
    id,
    lineNumber: p.lineNumber,
    path: p.key,
    value: p.value,
    contentType: p.contentType,
    parseError: null,
    status: { kind: "pending" },
  };
}

function annotate(row: ImportRow, existingPaths: Set<string>): AnnotatedRow {
  const pathError = row.parseError ? null : validatePath(row.path);
  const valueError = row.parseError ? null : validateValue(row.value);
  const conflict =
    !row.parseError &&
    pathError === null &&
    row.status.kind !== "done" &&
    existingPaths.has(row.path);
  return { ...row, pathError, valueError, conflict };
}

type Counts = {
  parseErrors: number;
  pathErrors: number;
  valueErrors: number;
  conflicts: number;
  readyToCreate: number;
};

function countByState(rows: ReadonlyArray<AnnotatedRow>): Counts {
  let parseErrors = 0;
  let pathErrors = 0;
  let valueErrors = 0;
  let conflicts = 0;
  let readyToCreate = 0;
  for (const r of rows) {
    if (r.parseError) {
      parseErrors += 1;
      continue;
    }
    if (r.pathError) pathErrors += 1;
    else if (r.valueError) valueErrors += 1;
    else if (r.conflict) conflicts += 1;
    else if (r.status.kind !== "done") readyToCreate += 1;
  }
  return { parseErrors, pathErrors, valueErrors, conflicts, readyToCreate };
}

function validateValue(value: string): string | null {
  if (value.length === 0) return "Value is required.";
  const bytes = new TextEncoder().encode(value).byteLength;
  if (bytes > MAX_VALUE_BYTES) {
    return `Value is ${formatBytes(bytes)} — max ${formatBytes(MAX_VALUE_BYTES)}.`;
  }
  if (value.length > MAX_PATH_LEN * 1024) return "Value is too large.";
  return null;
}

function maskValue(value: string): string {
  if (value.length === 0) return "";
  const visible = value.length <= 4 ? 0 : 2;
  if (visible === 0) return "•".repeat(Math.min(8, value.length));
  return value.slice(0, visible) + "•".repeat(Math.min(20, value.length - visible));
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function extractError(err: unknown): string {
  if (err instanceof WardkeyAPIError) {
    if (err.status === 409) return "Already exists.";
    if (err.code === "forbidden") return "No write access.";
    return err.message || err.code || "Request failed";
  }
  if (err instanceof Error) return err.message;
  return "Request failed";
}

export type { ImportRow };
